import Image from 'next/image';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import chevronDown from 'public/icons/chevronDown.svg';

import styles from './styles.module.scss';

type Props = {
  children: ReactNode | string;
  lines?: number;
  closeHeight?: number;
  openHeight?: number;
};

const ProductTextShowMore = ({
  children,
  lines,
  closeHeight: initialCloseHeight,
  openHeight: initialOpenHeight,
}: Props) => {
  const [showMore, setShowMore] = useState(false);
  const [closeHeight, setCloseHeight] = useState(initialCloseHeight);
  const [openHeight, setOpenHeight] = useState(initialOpenHeight);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const contentContainerHeight = useMemo(() => {
    if (typeof closeHeight === 'number' && typeof openHeight === 'number') {
      return showMore ? openHeight : closeHeight;
    }
    return 'auto';
  }, [closeHeight, openHeight, showMore]);
  const isShowMoreButtonVisible = useMemo(() => {
    if (typeof closeHeight === 'number' && typeof openHeight === 'number') {
      return true;
    }
    return false;
  }, [closeHeight, openHeight]);

  useEffect(() => {
    if (!textContainerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (!entry.target) return;
        const paragraphs = entry.target.querySelectorAll('p');
        const rectsLines: DOMRect[] = [];
        paragraphs.forEach(paragraph => {
          const range = document.createRange();
          range.setStart(paragraph, 0);
          range.setEnd(paragraph, paragraph.childNodes.length);
          const rects = range.getClientRects();
          rectsLines.push(...rects);
        });
        const closeHeight = rectsLines
          .slice(0, lines)
          .reduce((acc, line) => acc + line.height, 0);
        setOpenHeight(entry.target.clientHeight);
        setCloseHeight(closeHeight);
      }
    });
    observer.observe(textContainerRef.current);
  }, [children, lines]);

  useEffect(() => {
    if (typeof children === 'string') return;
    setOpenHeight(initialOpenHeight);
    setCloseHeight(initialCloseHeight);
  }, [children, initialCloseHeight, initialOpenHeight]);

  return (
    <div className={styles.container}>
      <div
        className={styles.contentContainer}
        style={{
          height: contentContainerHeight,
        }}
      >
        {typeof children === 'string' && (
          <div className={styles.textContainer} ref={textContainerRef}>
            <ReactMarkdown skipHtml={false} className={styles.text}>
              {children}
            </ReactMarkdown>
          </div>
        )}
        {typeof children !== 'string' && children}
        {isShowMoreButtonVisible && !showMore && (
          <div
            style={{
              boxShadow: '#fff 0px -3px 13px 16px',
              width: '100%',
              position: 'absolute',
              bottom: 0,
              left: 0,
            }}
          />
        )}
      </div>
      {isShowMoreButtonVisible && (
        <button
          className={styles.showMoreButton}
          onClick={() => setShowMore(prev => !prev)}
        >
          {showMore ? 'Zwiń' : 'Rozwiń więcej'}
          <Image
            src={chevronDown}
            alt="chevron down"
            style={{
              transform: showMore ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease-in-out',
            }}
          />
        </button>
      )}
    </div>
  );
};

export default ProductTextShowMore;
